import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { WorkoutAchievementType } from "../../../../common/enums";
import { WorkoutAchievementTypeShortLabels } from "../../../../common/labels";
import {
  IUseWorkoutAchievementsArgs,
  useWorkoutAchievements,
} from "./hooks/useWorkoutAchievements";
import { IWorkoutAchievement } from "./models/workoutAchievementsViewModel";
import { AchievementCard } from "./components/AchievementCard";
import styles from "./WorkoutAchievements.module.scss";

interface IWorkoutAchievementsProps extends IUseWorkoutAchievementsArgs {
  /** Opens the workout a badge was earned in (wired by WorkoutsOverview). */
  onOpenWorkout?: (achievement: IWorkoutAchievement) => void;
}

/**
 * The "Постижения" tab on the My Workouts page: summary counts, a badge-type filter
 * and the list of personal records (newest first), Hevy/Strong style. A record is the
 * first completed set on an exercise, or a completed set that beat the user's previous
 * best weight or single-set volume on it (workouts taken in date order); badges are
 * derived from the log, so they always match the logged sets.
 */
export const WorkoutAchievements = ({
  onOpenWorkout,
  ...args
}: IWorkoutAchievementsProps) => {
  const { summary, achievements, hasAny, isLoading, filter, setFilter } =
    useWorkoutAchievements(args);

  const filters: { value: WorkoutAchievementType | null; label: string }[] = [
    { value: null, label: "Всички" },
    {
      value: WorkoutAchievementType.MaxWeight,
      label: WorkoutAchievementTypeShortLabels[WorkoutAchievementType.MaxWeight],
    },
    {
      value: WorkoutAchievementType.MaxSetVolume,
      label: WorkoutAchievementTypeShortLabels[WorkoutAchievementType.MaxSetVolume],
    },
  ];

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.skeletons}>
          {Array(4)
            .fill(0)
            .map((_, index) => (
              <div className={styles.skeleton} key={index} />
            ))}
        </div>
      </div>
    );
  }

  if (!hasAny || !summary) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          <div className={styles.empty_orb}>
            <Icon
              type={IconType.Trophy}
              width="30px"
              height="30px"
              color="rgba(252, 225, 115, 0.95)"
            />
          </div>
          <h2 className={styles.empty_title}>Все още нямате постижения</h2>
          <p className={styles.empty_description}>
            Значка печелите с първата завършена серия на всяко упражнение и всеки
            път, когато завършена серия надмине предишния ви най-добър резултат за
            него — по тегло или по обем (кг × повторения).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.cards}>
        <div className={styles.card}>
          <span className={styles.card_value}>{summary.totalCount}</span>
          <span className={styles.card_label}>Значки</span>
        </div>
        <div className={styles.card}>
          <span className={styles.card_value}>{summary.maxWeightCount}</span>
          <span className={styles.card_label}>
            {WorkoutAchievementTypeShortLabels[WorkoutAchievementType.MaxWeight]}
          </span>
        </div>
        <div className={styles.card}>
          <span className={styles.card_value}>{summary.maxSetVolumeCount}</span>
          <span className={styles.card_label}>
            {WorkoutAchievementTypeShortLabels[WorkoutAchievementType.MaxSetVolume]}
          </span>
        </div>
      </div>

      <div className={styles.filter} role="tablist" aria-label="Тип значка">
        {filters.map((option) => (
          <button
            key={option.label}
            type="button"
            role="tab"
            aria-selected={filter === option.value}
            className={classNames(styles.filter_chip, {
              [styles.filter_chip_active]: filter === option.value,
            })}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className={styles.list}>
        {achievements.map((achievement) => (
          <AchievementCard
            key={`${achievement.setId}-${achievement.type}`}
            achievement={achievement}
            onClick={onOpenWorkout ? () => onOpenWorkout(achievement) : undefined}
          />
        ))}
      </div>
    </div>
  );
};
